/**
 * The challenge in front of the public forms.
 *
 * A form marked with `data-captcha` does not submit straight away. The script
 * asks the portal whether this visitor has to prove they are a person; if the
 * answer is no the form goes as it always did, and if it is yes a dialog opens
 * with a drawing to read.
 *
 * Solving the picture does not do the thing the form does. It hands back a
 * token, the token is written into the form as a hidden field, and the visitor
 * presses the button which continues - so the registration, the message or the
 * address is sent by them, at the moment they meant to send it.
 *
 * Cancelling closes the dialog and forgets the challenge. It does not reload
 * the page: whatever was typed into the form is still there, and pressing the
 * button again draws a new picture.
 */

import { HttpError, getJson, postJson } from "../core/http";
import { query, queryAll } from "../core/dom";
import { allowUnload } from "./unsaved";
import { text } from "../core/i18n";

/** What the portal answers when asked whether a form needs a challenge. */
interface NeededResponse {
    readonly required: boolean;
}

/** A freshly drawn challenge. */
interface ChallengeResponse {
    readonly token: string;
    readonly picture: string;
    readonly seconds: number;
    readonly tries: number;
}

/** How an answer went. */
interface OutcomeResponse {
    readonly solved: boolean;
    readonly tries: number;
}

const DIALOG_ID = "captcha-dialog";

const FIELD_NAME = "captcha_token";

/** Return the markup of one symbol of the sprite. */
function iconMarkup(name: string, size: number): string {
    return (
        `<svg class="icon" width="${size}" height="${size}" aria-hidden="true">` +
        `<use href="/static/assets/icons/icons.svg#${name}"></use></svg>`
    );
}

/** Return the dialog, building it the first time it is needed. */
export function captchaDialog(): HTMLDialogElement {
    const existing = query<HTMLDialogElement>(`#${DIALOG_ID}`);
    if (existing) {
        return existing;
    }

    const dialog = document.createElement("dialog");
    dialog.id = DIALOG_ID;
    dialog.className = "modal captcha";
    dialog.innerHTML = `
        <div class="modal__inner">
            <h2 class="modal__title captcha__title">
                ${iconMarkup("shield", 20)} ${text("js.captcha.title", "Overenie")}
            </h2>
            <p class="modal__text">${text("js.captcha.hint", "Prepíšte číslice z obrázka.")}</p>

            <div class="captcha__frame">
                <img class="captcha__picture" alt="${text("js.captcha.alt", "Obrázok s číslicami")}" width="280"
                    height="96" data-captcha-picture>
                <button type="button" class="button button--small captcha__again"
                    title="${text("js.captcha.again", "Nový obrázok")}"
                    aria-label="${text("js.captcha.again", "Nový obrázok")}" data-captcha-again>
                    ${iconMarkup("history", 15)}
                </button>
            </div>

            <div class="field captcha__field">
                <label for="captcha-answer">${text("js.captcha.label", "Číslice z obrázka")}</label>
                <input type="text" id="captcha-answer" inputmode="numeric" autocomplete="off" maxlength="8"
                    data-captcha-answer>
                <span class="field__hint" data-captcha-state></span>
            </div>

            <div class="modal__actions">
                <button type="button" class="button" data-captcha-cancel>
                    ${text("js.captcha.cancel", "Zrušiť")}
                </button>
                <button type="button" class="button button--primary" data-captcha-continue>
                    ${text("js.captcha.continue", "Pokračovať")}
                </button>
            </div>
        </div>
    `;
    document.body.appendChild(dialog);

    return dialog;
}

/** Return the token of the challenge which was solved, or nothing. */
async function askForChallenge(dialog: HTMLDialogElement, purpose: string): Promise<string | null> {
    const picture = query<HTMLImageElement>("[data-captcha-picture]", dialog);
    const answer = query<HTMLInputElement>("[data-captcha-answer]", dialog);
    const state = query<HTMLElement>("[data-captcha-state]", dialog);
    const again = query<HTMLButtonElement>("[data-captcha-again]", dialog);
    const cancel = query<HTMLButtonElement>("[data-captcha-cancel]", dialog);
    const proceed = query<HTMLButtonElement>("[data-captcha-continue]", dialog);

    if (!picture || !answer || !state || !again || !cancel || !proceed) {
        return null;
    }

    let token = "";
    let deadline = 0;
    let ticking = 0;

    const say = (message: string, wrong = false): void => {
        state.textContent = message;
        state.classList.toggle("is-wrong", wrong);
    };

    const draw = async (): Promise<void> => {
        answer.value = "";
        say(text("js.captcha.loading", "Kreslím obrázok…"));

        try {
            const challenge = await postJson<ChallengeResponse>(
                `/api/captcha?purpose=${encodeURIComponent(purpose)}`,
                {},
            );
            token = challenge.token;
            picture.src = challenge.picture;
            deadline = Date.now() + challenge.seconds * 1000;
            say("");
            answer.focus();
        } catch {
            token = "";
            say(text("js.captcha.error", "Obrázok sa nepodarilo načítať."), true);
        }
    };

    // The challenge stands for two minutes; the line under the field counts
    // them down so that nobody types into a picture which is already dead.
    const tick = (): void => {
        if (!deadline) {
            return;
        }

        const left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
        if (left === 0) {
            deadline = 0;
            say(text("js.captcha.expired", "Obrázok vypršal, vezmite si nový."), true);

            return;
        }

        if (!state.classList.contains("is-wrong")) {
            const minutes = Math.floor(left / 60);
            const seconds = `${left % 60}`.padStart(2, "0");
            say(text("js.captcha.remaining", "Zostáva {time}").replace("{time}", `${minutes}:${seconds}`));
        }
    };

    return new Promise<string | null>((resolve) => {
        const finish = (value: string | null): void => {
            window.clearInterval(ticking);
            again.onclick = null;
            cancel.onclick = null;
            proceed.onclick = null;
            answer.onkeydown = null;
            answer.oninput = null;
            dialog.close();
            resolve(value);
        };

        again.onclick = (): void => {
            void draw();
        };

        cancel.onclick = (): void => finish(null);

        proceed.onclick = (): void => {
            void (async (): Promise<void> => {
                if (!token) {
                    await draw();

                    return;
                }

                const typed = answer.value.trim();
                if (!typed) {
                    say(text("js.captcha.empty", "Prepíšte číslice z obrázka."), true);

                    return;
                }

                try {
                    const outcome = await postJson<OutcomeResponse>("/api/captcha/answer", { token, answer: typed });
                    if (outcome.solved) {
                        finish(token);

                        return;
                    }

                    if (outcome.tries <= 0) {
                        say(text("js.captcha.expired", "Obrázok vypršal, vezmite si nový."), true);
                        token = "";

                        return;
                    }

                    // A misread picture is drawn again rather than stared at:
                    // whatever made it hard to read is not going to get easier
                    // on the second look.
                    await draw();
                    say(text("js.captcha.wrong", "Nesedí to, tu je nový obrázok."), true);
                } catch (error) {
                    const message =
                        error instanceof HttpError && error.message
                            ? error.message
                            : text("js.captcha.error", "Obrázok sa nepodarilo načítať.");
                    say(message, true);
                }
            })();
        };

        answer.oninput = (): void => {
            state.classList.remove("is-wrong");
        };

        answer.onkeydown = (event: KeyboardEvent): void => {
            if (event.key === "Enter") {
                event.preventDefault();
                proceed.click();
            }
        };

        dialog.addEventListener("cancel", () => finish(null), { once: true });
        dialog.showModal();
        ticking = window.setInterval(tick, 1000);
        void draw();
    });
}

/** Write the solved token into the form and send it. */
function sendWith(form: HTMLFormElement, token: string): void {
    let field = form.querySelector<HTMLInputElement>(`input[name="${FIELD_NAME}"]`);
    if (!field) {
        field = document.createElement("input");
        field.type = "hidden";
        field.name = FIELD_NAME;
        form.appendChild(field);
    }

    field.value = token;
    form.dataset["captchaDone"] = "yes";
    allowUnload();
    // `submit()` rather than `requestSubmit()`: the second one raises the
    // event again and would ask for a second picture.
    form.submit();
}

/** Stop a form until its visitor has answered a picture. */
export function guardWithCaptcha(form: HTMLFormElement): void {
    const purpose = form.dataset["captcha"] ?? "";
    if (!purpose) {
        return;
    }

    form.addEventListener("submit", (event: Event) => {
        if (form.dataset["captchaDone"] === "yes") {
            return;
        }

        // Everything the browser itself refuses is refused before a picture
        // is drawn, or somebody reads five digits to be told their address
        // is missing an at sign.
        if (typeof form.reportValidity === "function" && !form.reportValidity()) {
            return;
        }

        event.preventDefault();

        void (async (): Promise<void> => {
            let needed = true;

            try {
                const answer = await getJson<NeededResponse>(
                    `/api/captcha/needed?purpose=${encodeURIComponent(purpose)}`,
                );
                needed = answer.required;
            } catch {
                // The portal decides for itself when the form arrives; if the
                // question could not be asked, send it and let it decide.
                needed = false;
            }

            if (!needed) {
                form.dataset["captchaDone"] = "yes";
                allowUnload();
                form.submit();

                return;
            }

            const token = await askForChallenge(captchaDialog(), purpose);
            if (token) {
                sendWith(form, token);
            }
        })();
    });
}

/** Wire every form which stands behind a challenge. */
export function initCaptchaForms(): void {
    for (const form of queryAll<HTMLFormElement>("form[data-captcha]")) {
        guardWithCaptcha(form);
    }
}
